/**
 * Centralized Action Registry for Frontend
 * 
 * This module provides a single source of truth for audit action types,
 * their display names, chip tones, and descriptions. Mirrors the backend
 * ActionRegistry to ensure consistent messaging across the application.
 */

export type ChipTone = 'brand' | 'positive' | 'caution' | 'urgent' | 'neutral';

export type ActionCategory =
  | 'draw_lifecycle'
  | 'file_management'
  | 'certificate'
  | 'winner'
  | 'collaboration'
  | 'system';

export interface ActionDefinition {
  actionType: string;
  displayName: string;
  descriptionTemplate: string;
  category: ActionCategory;
  chipTone: ChipTone;
}

/**
 * Complete registry of all audit action types.
 * Matches backend ActionRegistry for consistency.
 */
export const ACTION_REGISTRY: Record<string, ActionDefinition> = {
  // Draw Lifecycle Actions
  draw_created: {
    actionType: 'draw_created',
    displayName: 'Draw created',
    descriptionTemplate: "Draw '{draw_name}' (No: {draw_no}) created with {entry_method} entry method",
    category: 'draw_lifecycle',
    chipTone: 'neutral',
  },
  draw_updated: {
    actionType: 'draw_updated',
    displayName: 'Draw updated',
    descriptionTemplate: "Draw '{draw_name}' updated",
    category: 'draw_lifecycle',
    chipTone: 'caution',
  },
  draw_completed: {
    actionType: 'draw_completed',
    displayName: 'Draw completed',
    descriptionTemplate: "Draw '{draw_name}' completed successfully",
    category: 'draw_lifecycle',
    chipTone: 'positive',
  },
  draw_deleted: {
    actionType: 'draw_deleted',
    displayName: 'Draw archived',
    descriptionTemplate: "Draw '{draw_name}' archived",
    category: 'draw_lifecycle',
    chipTone: 'urgent',
  },
  draw_failed: {
    actionType: 'draw_failed',
    displayName: 'Draw failed',
    descriptionTemplate: "Draw '{draw_name}' (No: {draw_no}) failed during execution: {error_message}",
    category: 'draw_lifecycle',
    chipTone: 'urgent',
  },
  scheduled: {
    actionType: 'scheduled',
    displayName: 'Draw scheduled',
    descriptionTemplate: "Draw '{draw_name}' (No: {draw_no}) scheduled for execution at {scheduled_time}",
    category: 'draw_lifecycle',
    chipTone: 'caution',
  },

  // File Management Actions
  file_uploaded: {
    actionType: 'file_uploaded',
    displayName: 'File uploaded',
    descriptionTemplate: "File '{file_name}' uploaded to draw '{draw_name}'",
    category: 'file_management',
    chipTone: 'brand',
  },
  file_downloaded: {
    actionType: 'file_downloaded',
    displayName: 'File downloaded',
    descriptionTemplate: "File '{file_name}' downloaded",
    category: 'file_management',
    chipTone: 'positive',
  },
  file_deleted: {
    actionType: 'file_deleted',
    displayName: 'File deleted',
    descriptionTemplate: "File '{file_name}' deleted from draw '{draw_name}'",
    category: 'file_management',
    chipTone: 'urgent',
  },

  // Certificate Actions
  certificate_generated: {
    actionType: 'certificate_generated',
    displayName: 'Certificate generated',
    descriptionTemplate: "Certificate generated for draw '{draw_name}'",
    category: 'certificate',
    chipTone: 'positive',
  },
  certificate_downloaded: {
    actionType: 'certificate_downloaded',
    displayName: 'Certificate downloaded',
    descriptionTemplate: "Certificate downloaded for draw '{draw_name}'",
    category: 'certificate',
    chipTone: 'positive',
  },

  // Winner Actions
  winner_generated: {
    actionType: 'winner_generated',
    displayName: 'Winners generated',
    descriptionTemplate: "Winners generated for draw '{draw_name}' - {winner_count} main winners, {reserve_count} reserves",
    category: 'winner',
    chipTone: 'positive',
  },

  // Collaboration Actions
  comment_added: {
    actionType: 'comment_added',
    displayName: 'Comment added',
    descriptionTemplate: "Comment added to draw '{draw_name}'",
    category: 'collaboration',
    chipTone: 'brand',
  },
};

/**
 * Get human-readable display name for an action type.
 * Falls back to formatted action type if not found in registry.
 */
export function getActionDisplayName(actionType: string): string {
  const definition = ACTION_REGISTRY[actionType];
  if (definition) {
    return definition.displayName;
  }
  // Fallback: convert snake_case to Title Case
  return actionType
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Get chip tone for an action type.
 * Used for styling action chips in the UI.
 */
export function getActionChipTone(actionType: string): ChipTone {
  const definition = ACTION_REGISTRY[actionType];
  return definition ? definition.chipTone : 'neutral';
}

/**
 * Get all action types in a specific category.
 */
export function getActionsByCategory(category: ActionCategory): ActionDefinition[] {
  return Object.values(ACTION_REGISTRY).filter(def => def.category === category);
}

/**
 * Get all action types as options for dropdowns/filters.
 */
export function getAllActionOptions(): Array<{ label: string; value: string }> {
  return Object.values(ACTION_REGISTRY).map(def => ({
    label: def.displayName,
    value: def.actionType,
  }));
}

/**
 * Get action options grouped by category.
 * Useful for grouped select dropdowns.
 */
export function getGroupedActionOptions(): Record<ActionCategory, Array<{ label: string; value: string }>> {
  const grouped: Record<string, Array<{ label: string; value: string }>> = {};
  
  Object.values(ACTION_REGISTRY).forEach(def => {
    if (!grouped[def.category]) {
      grouped[def.category] = [];
    }
    grouped[def.category].push({
      label: def.displayName,
      value: def.actionType,
    });
  });
  
  return grouped as Record<ActionCategory, Array<{ label: string; value: string }>>;
}

/**
 * Category display names for UI.
 */
export const CATEGORY_LABELS: Record<ActionCategory, string> = {
  draw_lifecycle: 'Draw lifecycle',
  file_management: 'File management',
  certificate: 'Certificates',
  winner: 'Winner generation',
  collaboration: 'Collaboration',
  system: 'System',
};

/**
 * Map action types to decorative icons for PlxsChip.
 * Provides visual cues for different action types.
 * Returns undefined for actions without appropriate icon mappings.
 * 
 * Available icons: add, arrowClock, arrowDown, arrowUp, bin, checkInCircle, comments,
 * edit, download, upload, warning, star, file, clock, etc.
 */
export function getActionDecorativeIcon(actionType: string): string | undefined {
  const iconMap: Record<string, string> = {
    // Draw Lifecycle
    draw_created: 'add',
    draw_updated: 'edit',
    draw_completed: 'checkInCircle',
    draw_deleted: 'fileArchive',
    draw_failed: 'warning',
    scheduled: 'clock',
    draw_retry: 'arrowClock',
    
    // File Management
    file_uploaded: 'upload',
    file_downloaded: 'download',
    file_deleted: 'bin',
    
    // Certificate
    certificate_generated: 'file',
    certificate_downloaded: 'download',
    
    // Winner
    winner_generated: 'stars',  // Fixed: was 'star', should be 'stars'
    
    // Collaboration
    comment_added: 'comments',
  };
  
  return iconMap[actionType];
}

/**
 * Draw Status Configuration
 * Provides consistent status chip configuration across the application.
 */
export interface StatusConfig {
  label: string;
  tone: ChipTone;
  decorativeIcon?: string;
}

/**
 * Get status chip configuration for draw statuses.
 * Returns label, tone, and decorative icon for consistent PlxsChip rendering.
 */
export function getStatusConfig(status: string): StatusConfig {
  const normalizedStatus = status.toLowerCase();
  
  const statusMap: Record<string, StatusConfig> = {
    completed: {
      label: 'Completed',
      tone: 'positive',
      decorativeIcon: 'check',
    },
    draft: {
      label: 'Draft',
      tone: 'neutral',
      decorativeIcon: 'edit',
    },
    scheduled: {
      label: 'Scheduled',
      tone: 'caution',
      decorativeIcon: 'arrowClock',
    },
    failed: {
      label: 'Failed',
      tone: 'urgent',
      decorativeIcon: 'warning',
    },
  };
  
  return statusMap[normalizedStatus] || {
    label: status,
    tone: 'neutral',
  };
}
